import * as Mui from "@mui/material";
import dayjs, { Dayjs } from "dayjs";
import utc from "dayjs/plugin/utc";
import { useContext, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import SectionHeader from "../../../../../util/component/SectionHeader";
import { ContextOperation } from "../../../context/OperationContext";
import {
  availabilityReportActions,
  IPayloadAvailabilityByGroup,
} from "../service/availabilityReport.actions";
import { DrillDownLoader } from "./useLazyDrillDown";
import { IAvailabilityReport } from "../types/IAvailabilityReport";
import { FilterBox, IAvailabilityFilters } from "./FilterBox";
import { SingleLocationTable } from "./SingleLocationTable";
import { TotalSingleLocationTable } from "./TotalSingleLocationTable";
import EmptyState from "../../../../../util/component/EmptyState";
import useSnackbar from "../../../hooks/useSnackbar";
import {
  IARLocation,
  IARRegionGroup,
  IARRowsPerGroup,
  ISingleLocationAvailabilityReport,
} from "../types/ISingleLocationAvailabilityReport";

dayjs.extend(utc);

/**
 * Il backend lavora in giorni UTC (tronca startDate/endDate a mezzanotte UTC). Il DatePicker restituisce
 * invece una data nel fuso del browser: con `toISOString()` la mezzanotte locale di Roma (UTC+1)
 * diventava 23:00Z del giorno precedente e il report partiva (e finiva) un giorno prima.
 * Si invia quindi il giorno scelto "così com'è" come giorno UTC, indipendentemente dal fuso del browser
 * (stessa convenzione `.utc(true)` di AvailabilityReportOld).
 */
const toUtcDayBoundary = (d: Dayjs, boundary: "start" | "end"): string =>
  (boundary === "start" ? d.startOf("day") : d.endOf("day")).utc(true).toISOString();

/**
 * CR LPP-20500 RF-03 — blocco di una singola regione: sub-totale sempre visibile
 * e, a scomparsa dietro un pulsante, l'elenco dei DMS della regione caricato ON-DEMAND
 * (seconda chiamata scoped ai soli DMS della regione).
 */
type RegionSectionProps = {
  region: IARRegionGroup;
  todayInRange: boolean;
  onLoadDms: (
    locations: IARLocation[]
  ) => Promise<ISingleLocationAvailabilityReport[]>;
  /** CR LPP-20500 (drilldown scalabile): drill-down a richiesta del sub-totale regione. */
  onLoadRegionDrillDown: (
    locations: IARLocation[]
  ) => Promise<IARRowsPerGroup[] | null>;
  /** CR LPP-20500 (drilldown scalabile): drill-down a richiesta di un singolo DMS della regione. */
  onLoadDmsDrillDown: (
    location: IARLocation
  ) => Promise<IARRowsPerGroup[] | null>;
};

/**
 * CR LPP-20500 — pulsante "Mostra/Nascondi DMS/Piazzali" con caricamento ON-DEMAND
 * del dettaglio per-DMS. Riusabile sia sotto un sub-totale regione sia, quando la
 * regione non è un concetto applicabile (tenant senza regioni), direttamente sotto il TOTALE.
 */
type DmsLocationsToggleProps = {
  locations: IARLocation[];
  todayInRange: boolean;
  onLoadDms: (
    locations: IARLocation[]
  ) => Promise<ISingleLocationAvailabilityReport[]>;
  /** CR LPP-20500 (drilldown scalabile): drill-down a richiesta di un singolo DMS. */
  onLoadDmsDrillDown: (
    location: IARLocation
  ) => Promise<IARRowsPerGroup[] | null>;
};

const DmsLocationsToggle = ({
  locations,
  todayInRange,
  onLoadDms,
  onLoadDmsDrillDown,
}: DmsLocationsToggleProps) => {
  const { t } = useTranslation(["operation"]);
  const [expanded, setExpanded] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [dms, setDms] = useState<ISingleLocationAvailabilityReport[] | null>(
    null
  );

  const dmsCount = locations?.length ?? 0;

  const handleToggle = () => {
    const next = !expanded;
    setExpanded(next);
    // Carico i DMS solo alla prima espansione (poi resta in cache).
    if (next && dms === null && !loading) {
      setLoading(true);
      onLoadDms(locations ?? [])
        .then((list) => setDms(list))
        .catch(() => setDms([]))
        .finally(() => setLoading(false));
    }
  };

  if (dmsCount === 0) return null;

  return (
    <>
      <Mui.Button
        size="small"
        variant="text"
        onClick={handleToggle}
        disabled={loading}
        sx={{ mt: 1 }}
      >
        {`${
          expanded
            ? t("availabilityReport.hideDms")
            : t("availabilityReport.showDms")
        } (${dmsCount})`}
      </Mui.Button>
      <Mui.Collapse in={expanded} unmountOnExit>
        {loading ? (
          <Mui.Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
            <Mui.CircularProgress size={24} />
          </Mui.Box>
        ) : (
          <Mui.Stack spacing={2} sx={{ mt: 1 }}>
            {(dms ?? []).map((element) => (
              <SingleLocationTable
                key={element.location.locationId}
                data={element}
                todayInRange={todayInRange}
                onLoadDrillDown={() => onLoadDmsDrillDown(element.location)}
              />
            ))}
          </Mui.Stack>
        )}
      </Mui.Collapse>
    </>
  );
};

const RegionSection = ({
  region,
  todayInRange,
  onLoadDms,
  onLoadRegionDrillDown,
  onLoadDmsDrillDown,
}: RegionSectionProps) => {
  const { t } = useTranslation(["operation"]);

  return (
    <Mui.Box>
      <TotalSingleLocationTable
        title={region.region || t("availabilityReport.regionUnknown")}
        data={region.totalRowsByRegion}
        rowsByGroup={region.rowsByGroupByRegion}
        todayInRange={todayInRange}
        onLoadDrillDown={() => onLoadRegionDrillDown(region.locations ?? [])}
        // LPP-20500 (perf): in una ricerca nazione solo il TOTALE nasce aperto; le regioni si aprono a richiesta.
        defaultExpanded={false}
      />
      <DmsLocationsToggle
        locations={region.locations ?? []}
        todayInRange={todayInRange}
        onLoadDms={onLoadDms}
        onLoadDmsDrillDown={onLoadDmsDrillDown}
      />
    </Mui.Box>
  );
};

/**
 * Pagina Report Disponibilità — CR LPP-20500.
 *
 * Path FE: `operation/availability/report` (vedi `OperationMain.tsx`).
 *
 * Riferimenti:
 *  - requisito Filippo `change-requests/CR-DRIVALIA-REPORTDISP/requisito fe pagina report.txt`
 *  - specifica visiva drill-down 4 livelli:
 *    `change-requests/CR-DRIVALIA-REPORTDISP/assertix/output/render-planet-report.js`
 *  - `AvailabilityReportOld.tsx` per filtri/threshold/export (da portare incrementalmente)
 *  - `FiltersModal.tsx` di `operation/vehicles/list` per cascata car segment/class/ACRISS
 */

type Props = {};

const initialFilters = (): IAvailabilityFilters => ({
  startDate: dayjs(),
  endDate: dayjs().add(30, "day"),
  location: [],
  onlyCustomer: true,
  locationStatus: "ACTIVATED",
  carSegmentFilter: [],
  carClassFilter: [],
  acrissFilter: [],
  marketSegmentFilter: [],
});

const AvailabilityReport = (_props: Props) => {
  const { tenant, setLoading } = useContext(ContextOperation);
  const { t } = useTranslation(["operation"]);
  const snackbar = useSnackbar();

  const [filters, setFilters] = useState<IAvailabilityFilters>(initialFilters);

  const [data, setData] = useState<IAvailabilityReport>();

  /**
   * CR LPP-20500 — indica se il giorno corrente fa parte del range effettivamente
   * ricercato (quello dei dati mostrati). Se false (ricerca interamente nel passato o
   * nel futuro) le tabelle NON mostrano i gruppi colonna "oggi inizio giornata" e
   * "oggi tempo reale", ma solo i giorni del range richiesto.
   */
  const [todayInRange, setTodayInRange] = useState<boolean>(true);

  // CR LPP-20500: campi filtro comuni, condivisi tra ricerca principale e caricamento DMS on-demand.
  const commonFilterPayload = () => ({
    onlyCustomerAtDesk: filters.onlyCustomer,
    startDate: filters.startDate ? toUtcDayBoundary(filters.startDate, "start") : undefined,
    endDate: filters.endDate ? toUtcDayBoundary(filters.endDate, "end") : undefined,
    acrissFilter:
      filters.acrissFilter.length > 0
        ? filters.acrissFilter.map((a) => a.code)
        : undefined,
    carClassFilter:
      filters.carClassFilter.length > 0
        ? filters.carClassFilter.map((c) => c.descr)
        : undefined,
    carSegmentFilter:
      filters.carSegmentFilter.length > 0
        ? filters.carSegmentFilter.map((s) => s.descr)
        : undefined,
    commercialSegmentFilter:
      filters.marketSegmentFilter.length > 0
        ? filters.marketSegmentFilter.map((m) => m.id)
        : undefined,
  });

  /*
   * CR LPP-20500 (drilldown scalabile): contesto dell'ULTIMA ricerca eseguita. Le chiamate a richiesta
   * (DMS di una regione, drill-down) devono usare filtri e location di quella ricerca, non quelli
   * eventualmente modificati nel form dopo il click su "Cerca".
   */
  type PickUp = { displayName: string; locationId: string; locationType: string };
  const searchContext = useRef<{
    filters: ReturnType<typeof commonFilterPayload>;
    pickUpList: PickUp[];
    hasNationSelected: boolean;
  } | null>(null);

  const baseSearchPayload = () =>
    searchContext.current?.filters ?? commonFilterPayload();

  const locationToPickUp = (l: IARLocation): PickUp => ({
    displayName: l.locationName,
    locationId: l.locationId,
    locationType: l.locationType,
  });

  const showFetchError = (error: any) => {
    const isTimeout =
      error?.code === "ECONNABORTED" ||
      error?.response?.status === 504 ||
      (typeof error?.message === "string" &&
        error.message.toLowerCase().includes("timeout"));
    snackbar.show(
      "error",
      isTimeout
        ? t("availabilityReport.errorTimeout")
        : t("availabilityReport.errorGeneric")
    );
  };

  /** Chiamata a richiesta del drill-down; null in caso di errore (già mostrato all'utente). */
  const fetchDrillDown = (
    pickUpList: PickUp[],
    extra: Partial<IPayloadAvailabilityByGroup>
  ): Promise<IAvailabilityReport | null> =>
    availabilityReportActions()
      .getAvailabilityByGroup(tenant, {
        ...baseSearchPayload(),
        pickUpList,
        ...extra,
      })
      .catch((error) => {
        showFetchError(error);
        return null;
      });

  /**
   * Drill-down del TOTALE. Scelta B: il BE calcola come per la ricerca principale e restituisce solo il
   * drill-down del totale (includeDrillDown=TOTAL_ONLY), senza quello delle singole regioni/DMS.
   */
  const loadTotalDrillDown: DrillDownLoader = () => {
    const ctx = searchContext.current;
    if (!ctx) return Promise.resolve(null);
    return fetchDrillDown(
      ctx.pickUpList,
      ctx.hasNationSelected
        ? {
            groupBy: "REGION",
            includeLocationDetail: false,
            includeDrillDown: "TOTAL_ONLY",
          }
        : { groupBy: "NONE", includeDrillDown: "TOTAL_ONLY" }
    ).then((response) => response?.totalRowsByGroup ?? null);
  };

  /** Drill-down del sub-totale di una regione: i suoi DMS come ricerca "leggera", solo drill-down totale. */
  const loadRegionDrillDown = (
    locations: IARLocation[]
  ): Promise<IARRowsPerGroup[] | null> =>
    fetchDrillDown((locations ?? []).map(locationToPickUp), {
      groupBy: "REGION",
      includeLocationDetail: false,
      includeDrillDown: "TOTAL_ONLY",
    }).then((response) => response?.totalRowsByGroup ?? null);

  /** Drill-down di un singolo DMS (chiamata scoped a una sola location, quindi leggera). */
  const loadDmsDrillDown = (
    location: IARLocation
  ): Promise<IARRowsPerGroup[] | null> =>
    fetchDrillDown([locationToPickUp(location)], {
      groupBy: "NONE",
      includeDrillDown: "FULL",
    }).then(
      (response) =>
        response?.singleLocationAvailabilityReportResponses?.[0]?.rowsPerGroup ??
        null
    );

  /*
   * CR LPP-20500 (ricerca nazione leggera): carica ON-DEMAND il dettaglio dei DMS di una regione.
   * Riusa l'endpoint esistente con pickUpList = i DMS della regione e groupBy=NONE
   * (includeLocationDetail=true → dettaglio per-DMS completo, ma scoped a poche location → veloce).
   * CR LPP-20500 (drilldown scalabile): senza drill-down (includeDrillDown=NONE), caricato poi per DMS.
   */
  const fetchRegionDms = (locations: IARLocation[]) => {
    const pickUpList = (locations ?? []).map(locationToPickUp);
    return availabilityReportActions()
      .getAvailabilityByGroup(tenant, {
        ...baseSearchPayload(),
        pickUpList,
        groupBy: "NONE",
        includeLocationDetail: true,
        includeDrillDown: "NONE",
      })
      .then(
        (response: IAvailabilityReport) =>
          response.singleLocationAvailabilityReportResponses ?? []
      )
      .catch((error) => {
        const isTimeout =
          error?.code === "ECONNABORTED" ||
          error?.response?.status === 504 ||
          (typeof error?.message === "string" &&
            error.message.toLowerCase().includes("timeout"));
        snackbar.show(
          "error",
          isTimeout
            ? t("availabilityReport.errorTimeout")
            : t("availabilityReport.errorGeneric")
        );
        return [] as ISingleLocationAvailabilityReport[];
      });
  };

  const onClickProceed = () => {
    setLoading(true);
    if (filters.location.length === 0) {
      setData(undefined);
      setLoading(false);
      return;
    }

    // Il giorno corrente è nel range solo se DAL <= oggi <= AL.
    const today = dayjs();
    setTodayInRange(
      !!filters.startDate &&
        !!filters.endDate &&
        !today.isBefore(filters.startDate, "day") &&
        !today.isAfter(filters.endDate, "day")
    );

    const pickUpList: { displayName: string; locationId: string; locationType: string }[] = [];
    for (const loc of filters.location) {
      if (loc.element.locationList?.length > 0) {
        for (const subLoc of loc.element.locationList) {
          pickUpList.push({
            displayName: subLoc.displayName,
            locationId: subLoc.id,
            locationType: subLoc.locationType,
          });
        }
        continue;
      }
      pickUpList.push({
        displayName: loc.element.displayName,
        locationId: loc.element.id,
        locationType: loc.element.locationType,
      });
    }

    /*
     * CR LPP-20500 RF-03: i sub-totali per regione si producono SOLO quando la
     * selezione è "tutto il territorio" (nazione). Per DMS singoli o per un gruppo
     * geografico (regione/area/città) NON vanno mostrate le regioni: groupBy=NONE
     * → il report mostra solo TOTALE + i DMS selezionati (o i DMS della regione).
     */
    const hasNationSelected = filters.location.some(
      (loc) => loc.element.groupType === "NATION"
    );
    const groupBy = hasNationSelected ? "REGION" : "NONE";

    // CR LPP-20500 (drilldown scalabile): memorizzo filtri e location della ricerca per le chiamate a richiesta.
    const searchFilters = commonFilterPayload();
    searchContext.current = { filters: searchFilters, pickUpList, hasNationSelected };

    availabilityReportActions()
      .getAvailabilityByGroup(tenant, {
        ...searchFilters,
        pickUpList,
        // CR LPP-20500 RF-03: REGION solo per "tutto il territorio" (nazione), altrimenti NONE.
        groupBy,
        // CR LPP-20500 (ottimizzazione nazione): in modalità REGION non richiedo il dettaglio
        // per-DMS; i DMS si caricano on-demand al click su "Mostra DMS" (vedi fetchRegionDms).
        includeLocationDetail: hasNationSelected ? false : undefined,
        // CR LPP-20500 (drilldown scalabile): la ricerca iniziale NON porta il drill-down (carSegment →
        // carClass → ACRISS), che è la parte dominante del payload: si carica all'espansione.
        includeDrillDown: "NONE",
      })
      .then((response) => {
        setData(response);
        setLoading(false);
      })
      .catch((error) => {
        setLoading(false);
        setData(undefined);
        // CR LPP-20500: la ricerca per nazione può andare in timeout (richiesta pesante).
        // Mostro un popup di errore dedicato distinguendo il timeout dagli altri errori.
        const isTimeout =
          error?.code === "ECONNABORTED" ||
          error?.response?.status === 504 ||
          (typeof error?.message === "string" &&
            error.message.toLowerCase().includes("timeout"));
        snackbar.show(
          "error",
          isTimeout
            ? t("availabilityReport.errorTimeout")
            : t("availabilityReport.errorGeneric")
        );
      });
  };

  return (
    <>
      <SectionHeader title={t("availabilityReport.mainTitle")} />

      <Mui.Stack className="availabilityReportNew" p={2} spacing={2}>
        <FilterBox
          filters={filters}
          setFilters={setFilters}
          onClickProceed={onClickProceed}
        />

        {!data && (
          <Mui.Box sx={{ pt: { xs: 1, sm: 2, md: 4, lg: 8 } }}>
            <EmptyState
              title={t("availabilityReport.noLocationSelectedTitle")}
              content={t("availabilityReport.noLocationSelectedContent")}
            />
          </Mui.Box>
        )}

        {data?.totalRows && (
          <TotalSingleLocationTable
            data={data.totalRows}
            rowsByGroup={data.totalRowsByGroup}
            todayInRange={todayInRange}
            onLoadDrillDown={loadTotalDrillDown}
          />
        )}
        {(() => {
          const regionGroups = data?.regionalTotalsByRegion ?? [];
          const hasRegionGroups = regionGroups.length > 0;
          const hasRealRegions = regionGroups.some(
            (r) => !!(r.region && r.region.trim())
          );

          // Ricerca nazione con regioni reali (es. drivalia_it): sub-totali per regione.
          if (hasRegionGroups && hasRealRegions) {
            return regionGroups.map((region) => (
              <RegionSection
                key={region.region}
                region={region}
                todayInRange={todayInRange}
                onLoadDms={fetchRegionDms}
                onLoadRegionDrillDown={loadRegionDrillDown}
                onLoadDmsDrillDown={loadDmsDrillDown}
              />
            ));
          }

          // CR LPP-20500 — ricerca nazione con tenant in cui la regione non è applicabile:
          // niente sub-totale "Regione non specificata", solo TOTALE + toggle DMS/Piazzali.
          if (hasRegionGroups && !hasRealRegions) {
            const allLocations = regionGroups.flatMap((r) => r.locations ?? []);
            return (
              <DmsLocationsToggle
                locations={allLocations}
                todayInRange={todayInRange}
                onLoadDms={fetchRegionDms}
                onLoadDmsDrillDown={loadDmsDrillDown}
              />
            );
          }

          // Ricerca non-nazione (groupBy=NONE): dettaglio per-DMS diretto.
          return data?.singleLocationAvailabilityReportResponses.map(
            (element) => (
              <SingleLocationTable
                key={element.location.locationId}
                data={element}
                todayInRange={todayInRange}
                onLoadDrillDown={() => loadDmsDrillDown(element.location)}
              />
            )
          );
        })()}
      </Mui.Stack>
    </>
  );
};

export default AvailabilityReport;
